import * as React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { GraduationCap, BookOpen, Building2, Loader2, CheckCircle2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  LoadingScreen,
} from "@eduviaos/ui-kit";
import { ApiError, type LoginResponse } from "@eduviaos/shared";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

interface InviteContext {
  tenant_slug: string;
  tenant_name: string;
  tenant_type: "school" | "coaching_institute" | "study_centre";
  admin_email: string;
  admin_full_name: string;
  expires_at: string;
}

const TYPE_HERO: Record<InviteContext["tenant_type"], { label: string; icon: React.ReactNode }> = {
  school: {
    label: "School",
    icon: <GraduationCap className="size-5" aria-hidden="true" />,
  },
  coaching_institute: {
    label: "Coaching Institute",
    icon: <BookOpen className="size-5" aria-hidden="true" />,
  },
  study_centre: {
    label: "Study Centre",
    icon: <Building2 className="size-5" aria-hidden="true" />,
  },
};

const PASSWORD_MIN = 12;

export function AcceptInvitePage() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";
  const navigate = useNavigate();
  const { setSession, status } = useAuth();

  // ALL hooks must run on every render before any early-return branches —
  // React error #300 if the order changes between renders. Disable the
  // query when there's no token (`enabled: false` keeps it inert) and
  // handle the auth/short-token branches AFTER all hooks have been called.
  const contextQuery = useQuery({
    queryKey: ["onboarding", "invite", token],
    queryFn: () => api.get<InviteContext>(`/onboarding/invite/${token}`),
    retry: false,
    enabled: token.length > 0 && status !== "authed",
  });

  // If a session already exists (the principal opened this URL while
  // logged in to another tenant), funnel to the dashboard rather than
  // redeem on top of an existing token.
  if (status === "authed") return <Navigate to="/dashboard" replace />;
  if (!token) {
    return <InviteErrorView title="Invalid invite link" />;
  }
  if (contextQuery.isLoading || status === "loading") {
    return <LoadingScreen label="Loading invite…" />;
  }
  if (contextQuery.isError) {
    const apiErr = contextQuery.error instanceof ApiError ? contextQuery.error : null;
    return (
      <InviteErrorView
        title={
          apiErr?.code === "NOT_FOUND"
            ? "This invite is no longer available"
            : "Could not load invite"
        }
        message={
          apiErr?.code === "NOT_FOUND"
            ? "It may have already been used, expired, or revoked. Ask your account manager for a new link."
            : (apiErr?.message ?? "Network error. Try again in a moment.")
        }
      />
    );
  }

  const ctx = contextQuery.data!;
  return (
    <AcceptForm
      ctx={ctx}
      token={token}
      onSuccess={(resp) => {
        setSession({
          user: resp.user,
          access_token: resp.tokens.access_token,
        });
        navigate("/dashboard", { replace: true });
      }}
    />
  );
}

function AcceptForm({
  ctx,
  token,
  onSuccess,
}: {
  ctx: InviteContext;
  token: string;
  onSuccess: (resp: LoginResponse) => void;
}) {
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [clientError, setClientError] = React.useState<string | null>(null);

  const acceptMutation = useMutation({
    mutationFn: (newPassword: string) =>
      api.post<LoginResponse>(`/onboarding/invite/${token}/accept`, {
        password: newPassword,
      }),
    onSuccess,
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setClientError(null);
    if (password.length < PASSWORD_MIN) {
      setClientError(`Password must be at least ${PASSWORD_MIN} characters.`);
      return;
    }
    if (!/\d/.test(password) || !/[!@#$%^&*()_\-+=,.?/:;{}[\]|<>~]/.test(password)) {
      setClientError("Add at least one digit and one symbol so the password resists guessing.");
      return;
    }
    if (password !== confirm) {
      setClientError("Passwords don't match.");
      return;
    }
    acceptMutation.mutate(password);
  };

  const hero = TYPE_HERO[ctx.tenant_type];
  const serverError =
    acceptMutation.error instanceof ApiError
      ? extractServerErrorMessage(acceptMutation.error)
      : null;

  return (
    <main className="from-background via-background to-muted/40 grid min-h-svh place-items-center bg-gradient-to-b px-4 py-12">
      <div className="grid w-full max-w-md gap-6">
        <header className="grid gap-4 text-center">
          <div className="bg-card text-muted-foreground mx-auto inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-[0.18em]">
            <span className="bg-primary/15 text-primary grid size-5 place-items-center rounded-full">
              {hero.icon}
            </span>
            {hero.label}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Welcome to {ctx.tenant_name}
          </h1>
          <p className="text-muted-foreground text-sm">
            You're being set up as the primary administrator for{" "}
            <strong className="text-foreground font-medium">{ctx.tenant_name}</strong>. Choose a
            password to finish activating your account.
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Activate your account</CardTitle>
            <CardDescription>
              Signing in as <span className="text-foreground font-medium">{ctx.admin_email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="grid gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="full-name">Your name</Label>
                <Input
                  id="full-name"
                  value={ctx.admin_full_name}
                  readOnly
                  aria-readonly
                  className="bg-muted/40"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  autoFocus
                  autoComplete="new-password"
                  minLength={PASSWORD_MIN}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={`At least ${PASSWORD_MIN} characters with a digit + symbol`}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  autoComplete="new-password"
                  minLength={PASSWORD_MIN}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>

              {(clientError || serverError) && (
                <div
                  role="alert"
                  className="border-destructive/50 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
                >
                  {clientError ?? serverError}
                </div>
              )}

              <Button type="submit" disabled={acceptMutation.isPending} className="mt-1">
                {acceptMutation.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    Activating…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                    Set password & sign in
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-muted-foreground text-center text-xs">
          By activating you agree to EduViaOS's terms of service. Trouble with this link? Ask your
          account manager to reissue it.
        </p>
      </div>
    </main>
  );
}

function InviteErrorView({ title, message }: { title: string; message?: string }) {
  return (
    <main className="bg-muted/30 grid min-h-svh place-items-center px-4 py-12">
      <Card className="max-w-md text-center">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {message ? <CardDescription>{message}</CardDescription> : null}
        </CardHeader>
      </Card>
    </main>
  );
}

function extractServerErrorMessage(err: ApiError): string {
  switch (err.code) {
    case "VALIDATION_ERROR":
      return err.message || "Password didn't meet the requirements.";
    case "NOT_FOUND":
      return "This invite is no longer available. Ask your account manager for a new link.";
    case "INVITE_ALREADY_USED":
      return "This invite has already been used. Try signing in directly with your existing password.";
    case "INVITE_EXPIRED":
      return "This invite has expired. Ask your account manager to reissue.";
    default:
      return err.message || "Could not activate your account. Try again in a moment.";
  }
}
